import React, { useState } from 'react'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import { Strings, TFunction, useTranslation } from 'language/translator'
import type { TThinkingStepDto } from '@homepal/api-contract'

interface ThinkingSummaryProps {
  reasoningText?: string
  steps: TThinkingStepDto[]
  durationMs?: number | null
}

// Maps the (metric-scoped) chat tools to a human-readable label key.
const TOOL_LABEL_KEYS: Record<string, Strings<'chat'>> = {
  get_metric_schema: 'tool_loaded_schema',
  get_underlying_data: 'tool_fetched_data',
  aggregate_metric_by_dimension: 'tool_aggregated',
  find_dimension_values: 'tool_found_values'
}

const stepLabel = (step: TThinkingStepDto, t: TFunction) => {
  const labelKey = TOOL_LABEL_KEYS[step.tool]

  return labelKey ? t('chat', labelKey) : step.tool
}

// Single collapsed "what I did" row shown once the answer is finished.
// Expands to the model's reasoning followed by the tools it ran, with
// durations. Structured on purpose (not markdown): steps + timings.
const ThinkingSummary = (props: ThinkingSummaryProps) => {
  const { reasoningText, steps, durationMs } = props

  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  if (!reasoningText && steps.length === 0) return null

  const seconds = durationMs ? Math.round(durationMs / 1000) : null
  const count = steps.length
  const one = count === 1
  const summary =
    seconds !== null
      ? t(
        'chat',
        one ? 'thought_for_seconds_one' : 'thought_for_seconds',
        false,
        { seconds: String(seconds), count: String(count) }
      )
      : t('chat', one ? 'steps_short_one' : 'steps_short', false, {
        count: String(count)
      })

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'text.secondary',
          cursor: 'pointer',
          display: 'inline-flex',
          gap: 0.25,
          '&:hover': { color: 'text.primary' }
        }}
      >
        <Text color="inherit" variant="body2">
          {summary}
        </Text>
        <Icon
          fontSize="small"
          name={open ? 'ExpandMore' : 'ChevronRight'}
          sx={{ color: 'inherit' }}
        />
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            borderLeft: '2px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mt: 0.5,
            pl: 2,
            py: 0.5
          }}
        >
          {reasoningText ? (
            <Text
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
              variant="body2"
            >
              {reasoningText}
            </Text>
          ) : null}

          {steps.map((step, i) => (
            <Box key={i} sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <Text color="text.secondary" variant="body2">
                {stepLabel(step, t)}
              </Text>
              {step.duration_ms !== undefined ? (
                <Text color="text.disabled" variant="caption">
                  {step.duration_ms < 1000
                    ? `${step.duration_ms}ms`
                    : `${(step.duration_ms / 1000).toFixed(1)}s`}
                </Text>
              ) : null}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

export default ThinkingSummary
